
import React, { createContext } from 'react';
import { initializeApp, getApp, getApps } from "firebase/app";
import { getDatabase, ref, query, orderByChild, equalTo, update } from "firebase/database";
import { initializeAuth, getAuth, FacebookAuthProvider, GoogleAuthProvider, OAuthProvider, signInWithPhoneNumber, PhoneAuthProvider, RecaptchaVerifier, unlink, updatePhoneNumber, linkWithPhoneNumber, browserLocalPersistence, browserPopupRedirectResolver } from "firebase/auth";
import { getStorage, ref as stRef } from "firebase/storage";
import { getReactNativePersistence } from 'firebase/auth/react-native';

const FirebaseContext = createContext(null);

let firebase = {
    app: null,
    database: null,
    auth: null,
    storage: null,
}

// Function to get the current firebase instance
export const getFirebase = () => firebase;

const createFullStructure = (app, db, auth, storage, config) => {
    return {
        app: app,
        config: config,
        database: db,
        auth: auth,
        storage: storage,
        authRef: getAuth,
        facebookProvider: new FacebookAuthProvider(),
        googleProvider: new GoogleAuthProvider(),
        appleProvider: new OAuthProvider('apple.com'),
        phoneProvider: new PhoneAuthProvider(auth),
        RecaptchaVerifier: RecaptchaVerifier,
        signInWithPhoneNumber: signInWithPhoneNumber,
        updatePhoneNumber: updatePhoneNumber,
        unlink: unlink,
        linkWithPhoneNumber: linkWithPhoneNumber,
        facebookCredential: (token) => FacebookAuthProvider.credential(token),
        facebookCredentialFromResult: (token) => FacebookAuthProvider.credentialFromResult(token),
        mobileAuthCredential: (verificationId, code) => PhoneAuthProvider.credential(verificationId, code),
        usersRef: ref(db, 'users'),
        bookingRef: ref(db, 'bookings'),
        cancelreasonRef: ref(db, 'cancel_reason'),
        complainRef: ref(db, 'complain'),
        editComplainRef: (id) => ref(db, "complain/" + id),
        settingsRef: ref(db, 'settings'),
        carTypesRef: ref(db, 'cartypes'),
        carTypesEditRef: (id) => ref(db, "cartypes/" + id),
        carDocImage: (id) => stRef(storage, `cartypes/${id}`),
        promoRef: ref(db, 'promos'),
        promoEditRef: (id) => ref(db, "promos/" + id),
        notifyRef: ref(db, "notifications/"),
        notifyEditRef: (id) => ref(db, "notifications/" + id),
        singleUserRef: (uid) => ref(db, "users/" + uid),
        profileImageRef: (uid) => stRef(storage, `users/${uid}/profileImage`),
        verifyIdImageRef: (uid) => stRef(storage, `users/${uid}/verifyIdImage`),
        bookingImageRef: (bookingId, imageType) => stRef(storage, `bookings/${bookingId}/${imageType}`),
        driversRef: query(ref(db, "users"), orderByChild("usertype"), equalTo("driver")),
        driverDocsRef: (uid) => stRef(storage, `users/${uid}/license`),
        driverDocsRefBack: (uid) => stRef(storage, `users/${uid}/licenseBack`),
        subscriptionReceiptRef: (uid, subscriptionId) => stRef(storage, `subscriptions/${uid}/${subscriptionId}/receipt`),
        walletReceiptRef: (uid, receiptId) => stRef(storage, `wallet_receipts/${uid}/${receiptId}/receipt`),
        singleBookingRef: (bookingKey) => ref(db, "bookings/" + bookingKey),
        requestedDriversRef: (bookingKey) => ref(db, "bookings/" + bookingKey + "/requestedDrivers"),
        referralIdRef: (referralId) => query(ref(db, "users"), orderByChild("referralId"), equalTo(referralId)),
        trackingRef: (bookingId) => ref(db, 'tracking/' + bookingId),
        tasksRef: () => query(ref(db, 'bookings'), orderByChild('status'), equalTo('NEW')),
        singleTaskRef: (uid, bookingId) => ref(db, "bookings/" + bookingId + "/requestedDrivers/" + uid),
        bookingListRef: (uid, role) =>
            role == 'customer' ? query(ref(db, 'bookings'), orderByChild('customer'), equalTo(uid)) :
                (role == 'driver' ?
                    query(ref(db, 'bookings'), orderByChild('driver'), equalTo(uid))
                    :
                    ref(db, 'bookings')
                ),
        chatRef: (bookingId) => ref(db, 'chats/' + bookingId + '/messages'),
        withdrawRef: ref(db, 'withdraws/'),
        languagesRef: ref(db, "languages"),
        languagesEditRef: (id) => ref(db, "languages/" + id),
        walletHistoryRef: (uid) => ref(db, "walletHistory/" + uid),
        userNotificationsRef: (uid) => ref(db, "userNotifications/" + uid),
        userRatingsRef: (uid) => ref(db, "userRatings/" + uid),
        carsRef: (uid, role) => role == 'driver' ?
            query(ref(db, 'cars'), orderByChild('driver'), equalTo(uid))
            : (role == 'fleetadmin' ?
                query(ref(db, 'cars'), orderByChild('fleetadmin'), equalTo(uid))
                : ref(db, 'cars')
            ),
        carAddRef: ref(db, "cars"),
        carEditRef: (id) => ref(db, "cars/" + id),
        carImage: (id) => stRef(storage, `cars/${id}`),
        allLocationsRef: ref(db, "locations"),
        userLocationRef: (uid) => ref(db, "locations/" + uid),
        categoryRef: ref(db, 'categories'),
        categoryEditRef: (id) => ref(db, "categories/" + id),
        categoryImage: (id) => stRef(storage, `categories/${id}`),
        addressRef: (uid, id) => ref(db, "savedAddresses/" + uid + "/" + id),
        addressEditRef: (uid) => ref(db, "savedAddresses/" + uid),
        countryRef: ref(db, 'countries'),
        countryEditRef: (id) => ref(db, "countries/" + id),
        zonesRef: ref(db, 'zones'),
        zoneEditRef: (id) => ref(db, "zones/" + id),
        paymentSettingsRef: ref(db, "payment_settings"),
        editPaymentSettingRef: (id) => ref(db, "payment_settings/" + id),
        addMoneyRef: ref(db, "addmoney"),
        smtpRef: ref(db, "smtpdata"),
        // Document Management
        documentFieldRef: ref(db, "document_fields"),
        documentFieldEditRef: (id) => ref(db, "document_fields/" + id),
        documentUserRef: ref(db, "document_users"),
        documentUserEditRef: (id) => ref(db, "document_users/" + id),
        // Queue Settings Configuration
        queueSettingsRef: ref(db, "queue_settings"),
        // Bank Account Management
        bankFieldRequirementsRef: ref(db, "bank_field_requirements"),
        bankUserDetailsRef: ref(db, "bank_user_details"),
        bankUserDetailsEditRef: (id) => ref(db, "bank_user_details/" + id),
        bankUserDetailsByUserRef: (userId) => ref(db, "bank_user_details_by_user/" + userId),
        bankUserDetailsByTypeRef: (userType) => query(ref(db, "bank_user_details"), orderByChild("userType"), equalTo(userType)),
        // Card Management
        cardDetailsRef: ref(db, "card_details"),
        cardDetailsEditRef: (id) => ref(db, "card_details/" + id),
        cardDetailsByUserRef: (userId) => ref(db, "card_details_by_user/" + userId),
        cardDetailsByTypeRef: (userType) => query(ref(db, "card_details"), orderByChild("userType"), equalTo(userType)),
        // Support Management
        supportRequestsRef: () => ref(db, "support_requests"),
        supportRequestEditRef: (id) => ref(db, "support_requests/" + id),
        supportCategoriesRef: () => ref(db, "support_categories"),
        supportCategoryEditRef: (id) => ref(db, "support_categories/" + id),
        // Firebase functions
        addonsRef: ref(db, "settings"),
        update: update
    }
}

const FirebaseProvider = ({ config, children, AsyncStorage }) => {
    let app, auth, database, storage;

    if (!getApps().length) {
        try {
            app = initializeApp(config);
            if (typeof document !== 'undefined') {
                auth = initializeAuth(app, {
                    persistence: browserLocalPersistence,
                    popupRedirectResolver: browserPopupRedirectResolver,
                });
            }
            else {
                auth = initializeAuth(app, {
                    persistence: getReactNativePersistence(AsyncStorage),
                });
            }
            database = getDatabase(app);
            storage = getStorage(app);
        } catch (error) {
            console.log("Error initializing app: " + error);
        }
    } else {
        app = getApp();
        auth = getAuth(app);
        database = getDatabase(app);
        storage = getStorage(app);
    }

    try {
        firebase = createFullStructure(app, database, auth, storage, config);
          // console.log('✅ Firebase initialized successfully:', firebase ? 'YES' : 'NO'); // Removed repetitive log
  // console.log('🔍 Firebase carAddRef available:', firebase && firebase.carAddRef ? 'YES' : 'NO'); // Removed repetitive log
        
        // FIXED: Add validation for firebase object
        if (!firebase) {
            console.error('❌ Firebase object is null/undefined');
            throw new Error('Firebase initialization failed');
        }
        
        // FIXED: Validate update function exists
        if (!firebase.update) {
            console.error('❌ Firebase update function is missing');
            throw new Error('Firebase update function not available');
        }
        
        // FIXED: Validate carAddRef exists
        if (!firebase.carAddRef) {
            console.error('❌ Firebase carAddRef is missing');
            throw new Error('Firebase carAddRef not available');
        }
        
        return (
            <FirebaseContext.Provider value={firebase}>
                {children}
            </FirebaseContext.Provider>
        )
    } catch (error) {
        console.error('❌ Error in FirebaseProvider:', error);
        // FIXED: Return fallback provider with error handling
        return (
            <FirebaseContext.Provider value={null}>
                {children}
            </FirebaseContext.Provider>
        )
    }
}

export {
    firebase,
    FirebaseProvider,
    FirebaseContext
}